/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import type {
  CircleMember,
  ProtectedProfile,
  SafeCircleMode,
  SafeCircleNotificationType,
} from "@/types/safecircle";

export interface NotificationContext {
  type: SafeCircleNotificationType;
  walkerName?: string;
  destination?: string;
  childName?: string;
  profileName?: string;
  etaMinutes?: number;
  havenName?: string;
  requesterName?: string;
  // ── Session 4 additions ──
  deviationDetail?: string;
  deviationSeverity?: "low" | "medium" | "high" | "critical";
  sosStatus?: string;
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
}

export interface RecipientRule {
  includeRoles: ("guardian" | "peer" | "trusted_contact")[];
  requireOptIn?: boolean;
  escalationDelayMs?: number;
}

/**
 * Per-mode routing rules for each notification type. Returns which circle
 * roles receive the notification, plus any escalation delays.
 */
export function routeToRecipients(
  type: SafeCircleNotificationType,
  mode: SafeCircleMode,
): RecipientRule {
  switch (type) {
    case "ROUTE_STARTED":
    case "ROUTE_COMPLETED":
      if (mode === "protected") {
        return { includeRoles: ["guardian"] };
      }
      if (mode === "connected") {
        return { includeRoles: ["guardian", "peer"], requireOptIn: true };
      }
      return { includeRoles: ["trusted_contact"], requireOptIn: true };

    case "ROUTE_DEVIATION":
    case "DEVIATION_DETECTED":
      if (mode === "protected") {
        return { includeRoles: ["guardian"], escalationDelayMs: 0 };
      }
      if (mode === "connected") {
        return {
          includeRoles: ["guardian", "peer"],
          escalationDelayMs: 5 * 60 * 1000,
        };
      }
      return {
        includeRoles: ["trusted_contact"],
        escalationDelayMs: 5 * 60 * 1000,
        requireOptIn: true,
      };

    case "SOS_TRIGGERED":
    case "SOS_UPDATE":
    case "SOS_RESOLVED":
      return {
        includeRoles: ["guardian", "peer", "trusted_contact"],
        escalationDelayMs: 0,
      };

    case "WALK_ME_HOME_STARTED":
    case "WALK_ME_HOME_ARRIVED":
    case "WALK_ME_HOME_OVERDUE":
      return { includeRoles: ["guardian", "peer", "trusted_contact"] };

    case "SAFE_HAVEN_NEARBY":
      return { includeRoles: [] };

    case "GUARDIAN_REQUEST":
      return { includeRoles: ["guardian"] };

    case "CHILD_APPROACHING":
      return { includeRoles: ["guardian"] };
  }
}

/**
 * Filter a circle to the recipients for a given notification type.
 */
export function recipientsForCircle(
  circle: CircleMember[],
  type: SafeCircleNotificationType,
  profile: Pick<ProtectedProfile, "mode">,
): CircleMember[] {
  const rule = routeToRecipients(type, profile.mode);
  return circle.filter((m) => rule.includeRoles.includes(m.circleRole));
}

/**
 * Build a title/body payload for a notification. A t() function is supplied
 * so the caller controls i18n lookup.
 */
export function formatNotification(
  ctx: NotificationContext,
  t: (key: string, vars?: Record<string, string | number>) => string,
): NotificationPayload {
  switch (ctx.type) {
    case "ROUTE_STARTED":
      return {
        title: t("notifications.routeStarted", { name: ctx.walkerName ?? "" }),
        body: t("notifications.routeStartedBody", {
          destination: ctx.destination ?? "",
        }),
      };
    case "ROUTE_COMPLETED":
      return {
        title: t("notifications.routeCompleted", {
          name: ctx.walkerName ?? "",
        }),
        body: t("notifications.routeCompletedBody"),
      };
    case "ROUTE_DEVIATION":
      return {
        title: t("notifications.routeDeviation"),
        body: t("notifications.routeDeviationBody", {
          name: ctx.walkerName ?? "",
        }),
      };
    case "SOS_TRIGGERED":
      return {
        title: t("notifications.sosAlert", { name: ctx.walkerName ?? "" }),
        body: t("notifications.sosTriggeredBody"),
        icon: "🚨",
      };
    case "WALK_ME_HOME_STARTED":
      return {
        title: t("notifications.walkMeHomeStarted", {
          name: ctx.walkerName ?? "",
        }),
        body: t("notifications.walkMeHomeStartedBody", {
          destination: ctx.destination ?? "",
          eta: String(ctx.etaMinutes ?? 0),
        }),
      };
    case "WALK_ME_HOME_ARRIVED":
      return {
        title: t("notifications.walkArrived", { name: ctx.walkerName ?? "" }),
        body: t("notifications.walkMeHomeArrivedBody"),
      };
    case "WALK_ME_HOME_OVERDUE":
      return {
        title: t("notifications.walkOverdue", { name: ctx.walkerName ?? "" }),
        body: t("notifications.walkMeHomeOverdueBody"),
        icon: "⚠️",
      };
    case "SAFE_HAVEN_NEARBY":
      return {
        title: t("notifications.safeHavenNearby"),
        body: t("notifications.safeHavenNearbyBody", {
          haven: ctx.havenName ?? "",
        }),
      };
    case "GUARDIAN_REQUEST":
      return {
        title: t("notifications.guardianRequest"),
        body: t("notifications.guardianRequestBody", {
          name: ctx.requesterName ?? "",
        }),
      };
    case "CHILD_APPROACHING":
      return {
        title: t("notifications.childApproaching"),
        body: t("notifications.childApproachingBody", {
          name: ctx.childName ?? "",
        }),
      };
    case "DEVIATION_DETECTED":
      return {
        title: t("notifications.deviationDetected", {
          name: ctx.walkerName ?? "",
        }),
        body: t("notifications.deviationDetectedBody", {
          detail: ctx.deviationDetail ?? "",
          severity: ctx.deviationSeverity ?? "medium",
        }),
        icon: ctx.deviationSeverity === "critical" ? "🚨" : "⚠️",
      };
    case "SOS_UPDATE":
      return {
        title: t("notifications.sosUpdate", { name: ctx.walkerName ?? "" }),
        body: t("notifications.sosUpdateBody", {
          status: ctx.sosStatus ?? "",
        }),
      };
    case "SOS_RESOLVED":
      return {
        title: t("notifications.sosResolved", { name: ctx.walkerName ?? "" }),
        body: t("notifications.sosResolvedBody"),
      };
  }
}
